import Tooltip from './ReportTooltip'
import {
  Users,
  MapPin,
  ListChecks,
  Question,
  ShoppingCart,
  ArrowUUpLeft,
  Money,
  Truck,
  Tag
} from '@phosphor-icons/react'
import AnimatedNumber from '../../../components/AnimatedNumber'
import { formatAnimatedNumber } from '../../../helpers/animatedNumberHelpers'
import { formatMoney } from '../../../helpers/utils'
import { RouteVisitEffectivenessCard } from './RouteExecutionKpis'
import { buildRouteKpiValues } from '../helpers/routeKpiValues'
import { REPORT_HELP } from '../helpers/reportTextConstants'
import { formatReportRatio } from '../helpers/reportRatio'
import {
  getReportMotiveKinds,
  REPORT_ACTIVITY_KINDS,
  REPORT_ACTIVITY_LABELS
} from '../helpers/reportActivityConfig'
import ReportSection from './ReportSection'
import ReportDistributionCard from './ReportDistributionCard'

export const REPORT_TOTALS = [
  ['order', 'Total pedidos', ShoppingCart, 'pedidos'],
  ['refund', 'Total devoluciones', ArrowUUpLeft, 'devoluciones'],
  ['charge', 'Total cobros', Money, 'cobros'],
  ['delivery', 'Total entregas', Truck, 'entregas'],
  ['quote', 'Cotizaciones', Tag, 'cotizaciones']
]
const help = (text) => (
  <Tooltip title={text} arrow>
    <span className="report-help" tabIndex={0} aria-label={text}>
      <Question size={15} aria-hidden="true" />
    </span>
  </Tooltip>
)

// Missing activity values use the empty presentation. A failed cards request
// uses the explicit error state with a retry action instead of showing zeros.
const numericFields = [
  'total_in_route_clients',
  'total_visited_in_route_clients',
  'out_of_route_clients',
  'total_visited_clients',
  'active_users_count',
  'geofence_activities_count',
  'total_activities_count',
  'pos_task_activity_done_task_codes_count',
  'pos_task_activity_total_clients_count',
  ...REPORT_ACTIVITY_KINDS.summary.flatMap((kind) => [`total_${kind}`, `total_${kind}_count`])
]

export default function ReportOverview({
  kpiData,
  activeTab = 'summary',
  cardsLoadError = false,
  onRetryCards
}) {
  if (cardsLoadError) {
    return (
      <div className="report-overview report-overview--error" role="alert">
        <p>No se pudieron cargar los indicadores del periodo.</p>
        {onRetryCards ? (
          <button type="button" className="btn btn-link" onClick={onRetryCards}>
            Intentar de nuevo
          </button>
        ) : null}
      </div>
    )
  }

  const source = (Array.isArray(kpiData) ? kpiData[0] : kpiData) || {}
  const data = {
    ...source,
    ...Object.fromEntries(numericFields.map((key) => [key, source[key] ?? 0]))
  }
  const values = buildRouteKpiValues(data)
  const totals = REPORT_TOTALS.filter(([key]) => REPORT_ACTIVITY_KINDS[activeTab]?.includes(key))
  const motiveKinds = getReportMotiveKinds(activeTab)
  const groups = getReportMotiveKinds(activeTab, data.available_activities).map((key) => ({
    key,
    label: REPORT_ACTIVITY_LABELS[key],
    data: data[`no_visit_report_motives_${key}`] || {}
  }))
  const statistics = [
    {
      title: 'Usuarios activos',
      Icon: Users,
      value: data.active_users_count,
      label: 'Usuarios con actividad',
      tooltip: REPORT_HELP.active
    },
    {
      title: 'Actividad en geocerca',
      Icon: MapPin,
      value: data.geofence_activities_count,
      valueFormat: (value) =>
        formatReportRatio(value, data.total_activities_count, formatAnimatedNumber),
      label: 'Actividades realizadas dentro de geocerca',
      tooltip: REPORT_HELP.geofenceKpi
    },
    ...(['summary', 'pos'].includes(activeTab)
      ? [
          {
            title: 'Tareas en punto de venta',
            Icon: ListChecks,
            value: data.pos_task_activity_done_task_codes_count,
            denominator: data.pos_task_activity_total_clients_count,
            label: 'Tareas ejecutadas',
            tooltip: REPORT_HELP.tasks
          }
        ]
      : [])
  ]
  return (
    <div className="report-overview">
      <div className="report-visit-effectiveness">
        <RouteVisitEffectivenessCard
          values={{ ...values, info: REPORT_HELP.visitEffectiveness }}
          collapsible
        />
      </div>
      <ReportSection title="KPIs totales" subtitle="Usuarios, actividad y totales del periodo">
        <div className="report-stat-grid">
          {statistics.map(({ title, Icon, value, valueFormat, denominator, label, tooltip }) => (
            <article className="report-surface report-stat-card" key={title}>
              <div className="report-panel">
                <h3>
                  <span className="report-icon">
                    <Icon size={17} />
                  </span>
                  {title}
                </h3>
                <div className="report-stat-value">
                  <strong>
                    <AnimatedNumber value={value} format={valueFormat} />
                    {denominator !== undefined && (
                      <>
                        /<AnimatedNumber value={denominator} />
                      </>
                    )}
                  </strong>
                  <span>
                    <span>{label}</span>
                    {help(tooltip)}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
        {totals.length > 0 && (
          <div className="report-surface report-totals">
            {totals.map(([kind, label, Icon, unit]) => (
              <article key={kind}>
                <h3>
                  <span className="report-icon">
                    <Icon size={15} />
                  </span>
                  {label}
                </h3>
                <strong>
                  <AnimatedNumber value={data[`total_${kind}`]} format={formatMoney} />
                </strong>
                <small>
                  {`${Number(data[`total_${kind}_count`]).toLocaleString('es')} ${unit}`}
                </small>
              </article>
            ))}
          </div>
        )}
      </ReportSection>
      {(motiveKinds.length > 0 || ['summary', 'charges'].includes(activeTab)) && (
        <ReportSection
          title="Motivos y gráficas"
          subtitle="Distribución de motivos y cobros del periodo"
        >
          <div className="report-charts-grid">
            {motiveKinds.length > 0 && (
              <ReportDistributionCard
                title="Motivos de no visita"
                description="Motivos registrados cuando no se concretó la visita, por tipo de visita."
                groups={groups}
              />
            )}
            {['summary', 'charges'].includes(activeTab) && (
              <ReportDistributionCard
                title="Métodos de pago usados"
                description="Monto cobrado por método de pago. La suma es igual al Total cobros."
                groups={[{ key: 'payments', data: data.payment_methods_summary || {} }]}
                money
                allowDonut
              />
            )}
          </div>
        </ReportSection>
      )}
    </div>
  )
}
